import React from 'react';
import appNavigationNames, { ProfilesTabParamList, withActiveTab } from '../../utils/appNavigationNames';
import { getHeader, mobileHeaderConfigs } from '../v2BaseComponents/Header';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { screens } from '../screens/Tabs/Profiles';
import { getIntl } from '../../utils/intl';
import { MaybeDrawerLayout } from '../shared/MaybeSideNavLayout';
import { colors } from '../../utils/theme';
import { useCapturedUrlUTMParameters } from '../../hooks/useUrlUTMParameters';
import ProfileSideTabNavigation from '../shared/ProfileSideTabNavigation';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../utils/sharedMessages';

const Stack = createNativeStackNavigator();

const intl = getIntl('profiles-navigator', {
    root: 'Profiles',
    addProfile: 'Add Profile',
    editProfile: 'Profile Info',
    contactDetails: 'Edit Profile',
    emergencyContacts: 'Emergency Contacts',
    addEmergencyContact: 'Add Emergency Contact',
    editEmergencyContact: 'Edit Emergency Contact',
    careTeam: 'Care Team',
    addCareTeam: 'Add Care Team',
    careTeamMember: 'Care Team Member',
    editCareTeamMember: 'Edit Care Team Member',
    copyCareTeamMember: 'Duplicate Care Team Member',
    addRelatedCareTeamMember: 'Add Related Care Team Member',
    inviteCollaborator: 'Invite Collaborator',
    inviteDetails: 'Invite Details',
    collaborator: 'Collaborator',
    addTransferRequest: 'Add Transfer Request',
    transferRequest: 'Transfer Request',
    transferRequestResponse: 'Transfer Request Response',
    addCard: 'Add Card',
    editCard: 'Edit Card',
    data: '{categoryName}',
    dataDetails: '{categoryName} Details',
    addData: 'Add {categoryName}',
    editData: 'Edit {categoryName}',
    addPortal: 'Add Portal',
    addFastenPortal: 'Connect with Fasten',
    addTefcaConnection: 'Connect via TEFCA',
    note: 'Note',
    addNote: 'Add Note',
    editNote: 'Edit Note',
    transferNote: 'Move Note',
    profile: 'Profile',
    invite: 'Invitation',
    portalConnection: 'Portal Connection Details',
    fastenPortalConnection: 'Fasten Connection Details',
    bulkUpdate: 'Bulk Update',
    reports: 'Reports',
    copyNote: 'Copy Note',
    sharedReport: 'Shared Report',
    addSharedReport: 'Create Shared Report',
    files: 'Files',
    file: 'File',
    chat: 'Chat Assistant',
    labs: 'Labs',
    lab: 'Lab',
    clientProfile: 'Client Profile',
    editClientProfileGroups: 'Edit Client Profile Groups',
    clientRecord: 'Client Record',
    editClientRecord: 'Edit Client Record',
    addClientRecord: 'Add Client Record',
    addClientInvite: 'Invite New Client',
    clientInvitation: 'Client Invitation',
    addClientRecordInvite: 'Invite Client',
    addClientRecordNoteScreen: 'Add Client Record Note',
    clientRecordNoteDetailsScreen: 'Client Record Note Details',
    editClientRecordNoteScreen: 'Edit Client Record Note',
    clientRecordNotesScreen: 'Client Record Notes',
    copyOrgNotesToClients: 'Share Note',
    copyOrgCareTeamMemberToClients: 'Share Care Team Member',
    clientProfileHistory: 'Client Profile History',
    sendClientsMessage: 'Send Clients Message',
    messagePreferences: 'Message Preferences',
    clientsActivity: 'Clients Activity',
    orgInviteAcceptSuccess: 'Success',
    activityDetails: 'Client Activity Details',
    profileActivity: 'Profile Activity',
    profileActivityDetails: 'Profile Activity Details',
    messageProfile: 'Message Profile',
    clientActivityHistory: 'Client Activity History',
    copyVisitToClients: 'Share Visit',
    access: 'Access',
    connect: 'Connect',
    profileNotes: 'Profile Notes',
    bulkAddClientRecords: 'Bulk Add Client Records',
    addSuggestedFolders: 'Add Suggested Folders',
    editClientInvitation: 'Edit Client Invitation',
    bulkAddClientProfilesGroups: 'Bulk Add Clients To Groups',
    editClientBillingInformation: 'Edit Client Billing Information',
    clientInvoiceItems: 'Client Invoice Items',
    collaboratorInvoice: 'Collaborator Invoice',
    codeStatus: 'Code Status',
    legalDecisionMaker: 'Legal Decision Maker',
    bulkClientInvite: 'Bulk Client Invite',
    messages: 'Messages',
    sendNote: 'Share Note',
    subscribeToVisitInvites: 'Subscribe to Visit Invites',
});

const ProfilesNavigation = () => {
    const isMobile = useIsMobileLayout();
    const capturedUrlUTMParameters = useCapturedUrlUTMParameters();

    return (
        <MaybeDrawerLayout>
            <ProfileSideTabNavigation>
                <Stack.Navigator
                    initialRouteName={appNavigationNames.profiles.root}
                    screenOptions={{
                        contentStyle: {
                            width: '100%',
                            height: '100%',
                            backgroundColor: isMobile ? colors.Background : colors.White,
                        },
                    }}
                >
                    <Stack.Screen
                        options={{
                            header: getHeader({ mobile: mobileHeaderConfigs.logo, breadCrumbs: [] }),
                            title: intl('root'),
                            contentStyle: { backgroundColor: colors.Background },
                        }}
                        name={appNavigationNames.profiles.root}
                        component={screens.ProfilesRootScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'add-profile'],
                            }),
                            title: intl('addProfile'),
                        }}
                        name={appNavigationNames.profiles.add}
                        component={screens.AddProfileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile'],
                            }),
                        }}
                        name={appNavigationNames.profiles.profile}
                        component={screens.ProfileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'edit-profile'],
                            }),
                            title: intl('editProfile'),
                        }}
                        name={appNavigationNames.profiles.edit}
                        component={screens.EditProfileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'edit-profile', 'legal-decision-maker'],
                            }),
                            title: intl('legalDecisionMaker'),
                        }}
                        name={appNavigationNames.profiles.editLegalDecisionMaker}
                        component={screens.EditLegalDecisionMakerScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'edit-profile', 'code-status'],
                            }),
                            title: intl('codeStatus'),
                        }}
                        name={appNavigationNames.profiles.editCodeStatus}
                        component={screens.EditCodeStatusScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'contact-details'],
                            }),
                            title: intl('contactDetails'),
                        }}
                        name={appNavigationNames.profiles.contactDetails}
                        component={screens.ProfileContactDetailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'emergency-contacts'],
                            }),
                            title: intl('emergencyContacts'),
                        }}
                        name={appNavigationNames.profiles.emergencyContacts}
                        component={screens.EmergencyContactListScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile',
                                    'emergency-contacts',
                                    'emergency-contact',
                                    'edit-emergency-contact',
                                ],
                            }),
                            title: intl('editEmergencyContact'),
                        }}
                        name={appNavigationNames.profiles.editEmergencyContact}
                        component={screens.EmergencyContactEditScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile',
                                    'emergency-contacts',
                                    'add-emergency-contact',
                                ],
                            }),
                            title: intl('addEmergencyContact'),
                        }}
                        name={appNavigationNames.profiles.createEmergencyContact}
                        component={screens.EmergencyContactCreateScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'care-team'],
                            }),
                            title: intl('careTeam'),
                        }}
                        name={appNavigationNames.profiles.careTeam}
                        component={screens.CareTeamScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'care-team', 'add-care-team'],
                            }),
                            title: intl('addCareTeam'),
                        }}
                        name={appNavigationNames.profiles.addCareTeam}
                        component={screens.AddCareTeamScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'care-team', 'care-team-member'],
                            }),
                            title: intl('careTeamMember'),
                        }}
                        name={appNavigationNames.profiles.careTeamMember}
                        component={screens.CareTeamMemberScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile',
                                    'care-team',
                                    'care-team-member',
                                    'edit-care-team-member',
                                ],
                            }),
                            title: intl('editCareTeamMember'),
                        }}
                        name={appNavigationNames.profiles.editCareTeamMember}
                        component={screens.EditCareTeamMemberScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile',
                                    'care-team',
                                    'care-team-member',
                                    'copy-care-team-member',
                                ],
                            }),
                            title: intl('copyCareTeamMember'),
                        }}
                        name={appNavigationNames.profiles.copyCareTeamMember}
                        component={screens.CopyCareTeamMemberScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile',
                                    'care-team',
                                    'care-team-member',
                                    'add-related-care-team-member',
                                ],
                            }),
                            title: intl('addRelatedCareTeamMember'),
                        }}
                        name={appNavigationNames.profiles.addRelatedCareTeamMember}
                        component={screens.AddRelatedCareTeamMemberScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'invite-collaborator'],
                            }),
                            title: intl('inviteCollaborator'),
                        }}
                        name={appNavigationNames.profiles.inviteCollaborator}
                        component={screens.InviteCollaboratorScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'invite-details'],
                            }),
                            title: intl('inviteDetails'),
                        }}
                        name={appNavigationNames.profiles.inviteDetails}
                        component={screens.InviteDetailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile-from-invitation', 'invitation'],
                            }),
                            title: intl('invite'),
                        }}
                        name={appNavigationNames.profiles.invite}
                        component={screens.InviteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'collaborators', 'collaborator'],
                            }),
                            title: intl('collaborator'),
                        }}
                        name={appNavigationNames.profiles.collaborator}
                        component={screens.CollaboratorScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'collaborators', 'collaborator', 'invoice'],
                            }),
                            title: intl('collaboratorInvoice'),
                        }}
                        name={appNavigationNames.profiles.collaboratorInvoice}
                        component={screens.CollaboratorInvoiceScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'add-transfer-request'],
                            }),
                            title: intl('addTransferRequest'),
                        }}
                        name={appNavigationNames.profiles.addTransferRequest}
                        component={screens.AddTransferRequestScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'transfer-requests'],
                            }),
                            title: intl('transferRequest'),
                        }}
                        name={appNavigationNames.profiles.transferRequest}
                        component={screens.TransferRequestScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile-from-transfer-request',
                                    'transfer-requests',
                                    'transfer-request-response',
                                ],
                            }),
                            title: intl('transferRequestResponse'),
                        }}
                        name={appNavigationNames.profiles.transferRequestResponse}
                        component={screens.TransferRequestResponseScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'cards', 'add-card'],
                            }),
                            title: intl('addCard'),
                        }}
                        name={appNavigationNames.profiles.addCard}
                        component={screens.AddCardScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'cards', 'edit-card'],
                            }),
                            title: intl('editCard'),
                        }}
                        name={appNavigationNames.profiles.editCard}
                        component={screens.EditCardScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={({ route }) => ({
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'data'],
                            }),
                            title: intl('data', {
                                categoryName: pluralResourceCategoryMessages(
                                    (route.params as ProfilesTabParamList['data']).category,
                                ),
                            }),
                        })}
                        name={appNavigationNames.profiles.data}
                        component={screens.DataScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={({ route }) => ({
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'data', 'data-details'],
                            }),
                            title: intl('dataDetails', {
                                categoryName: resourceCategoryMessages(
                                    (route.params as ProfilesTabParamList['data-details']).category,
                                ),
                            }),
                        })}
                        name={appNavigationNames.profiles.dataDetails}
                        component={screens.DataDetailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={({ route }) => ({
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'data', 'add-data'],
                            }),
                            title: intl('addData', {
                                categoryName: resourceCategoryMessages(
                                    (route.params as ProfilesTabParamList['add-data']).category,
                                ),
                            }),
                        })}
                        name={appNavigationNames.profiles.addData}
                        component={screens.AddDataScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={({ route }) => ({
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'data', 'edit-data'],
                            }),
                            title: intl('editData', {
                                categoryName: resourceCategoryMessages(
                                    (route.params as ProfilesTabParamList['edit-data']).category,
                                ),
                            }),
                        })}
                        name={appNavigationNames.profiles.editData}
                        component={screens.EditDataScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'portals', 'add-portal'],
                            }),
                            title: intl('addPortal'),
                        }}
                        name={appNavigationNames.profiles.addPortal}
                        component={screens.AddPortalScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'portals', 'add-fasten-portal'],
                            }),
                            title: intl('addFastenPortal'),
                        }}
                        name={appNavigationNames.profiles.addFastenPortal}
                        component={screens.AddFastenPortalScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'portals', 'add-tefca-connection'],
                            }),
                            title: intl('addTefcaConnection'),
                        }}
                        name={appNavigationNames.profiles.addTefcaConnection}
                        component={screens.AddTefcaConnectionScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNote,
                                breadCrumbs: ['profiles-root', 'profile-from-note-id', 'profile-notes', 'profile-note'],
                            }),
                            title: intl('note'),
                        }}
                        name={appNavigationNames.profiles.note}
                        component={screens.NoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNote,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile-from-note-id',
                                    'profile-notes',
                                    'profile-note',
                                    'send-note',
                                ],
                            }),
                            title: intl('sendNote'),
                        }}
                        name={appNavigationNames.profiles.sendNote}
                        component={screens.SendNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile-from-note-destination',
                                    'profile-notes-without-note-id',
                                    'add-profile-note',
                                ],
                            }),
                            title: intl('addNote'),
                        }}
                        name={appNavigationNames.profiles.addNote}
                        component={screens.AddNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoteNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile-from-note-id',
                                    'profile-notes',
                                    'profile-note',
                                    'edit-profile-note',
                                ],
                            }),
                            title: intl('editNote'),
                        }}
                        name={appNavigationNames.profiles.editNote}
                        component={screens.EditNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoteNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile-from-note-id',
                                    'profile-notes',
                                    'profile-note',
                                    'transfer-note',
                                ],
                            }),
                            title: intl('transferNote'),
                        }}
                        name={appNavigationNames.profiles.transferNote}
                        component={screens.TransferNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'portals', 'portal-connection'],
                            }),
                            title: intl('portalConnection'),
                        }}
                        name={appNavigationNames.profiles.portalConnection}
                        component={screens.PortalConnectionScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'portals', 'fasten-portal-connection'],
                            }),
                            title: intl('fastenPortalConnection'),
                        }}
                        name={appNavigationNames.profiles.fastenPortalConnection}
                        component={screens.FastenPortalConnectionScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'data', 'data-bulk-update'],
                            }),
                            title: intl('bulkUpdate'),
                        }}
                        name={appNavigationNames.profiles.dataBulkUpdate}
                        component={screens.DataBulkUpdate}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'reports'],
                            }),
                            title: intl('reports'),
                        }}
                        name={appNavigationNames.profiles.reports}
                        component={screens.ReportsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoteNoChatButton,
                                breadCrumbs: [
                                    'profiles-root',
                                    'profile-from-note-id',
                                    'profile-notes',
                                    'profile-note',
                                    'copy-profile-note',
                                ],
                            }),
                            title: intl('copyNote'),
                        }}
                        name={appNavigationNames.profiles.copyNote}
                        component={screens.CopyNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'reports', 'add-shared-report'],
                            }),
                            title: intl('addSharedReport'),
                        }}
                        name={appNavigationNames.profiles.addSharedReport}
                        component={screens.AddSharedReportScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'reports', 'shared-report'],
                            }),
                            title: intl('sharedReport'),
                        }}
                        name={appNavigationNames.profiles.sharedReport}
                        component={screens.SharedReportScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'files'],
                            }),
                            title: intl('files'),
                        }}
                        name={appNavigationNames.profiles.files}
                        component={screens.FilesScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'files', 'file'],
                            }),
                            title: intl('file'),
                        }}
                        name={appNavigationNames.profiles.file}
                        component={screens.FileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                                breadCrumbs: ['profiles-root', 'profile', 'chat'],
                            }),
                            title: intl('chat'),
                        }}
                        name={appNavigationNames.profiles.chat}
                        component={screens.ChatScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'date-labs'],
                            }),
                            title: intl('labs'),
                        }}
                        name={appNavigationNames.profiles.dateLabs}
                        component={screens.DateLabsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'lab-history'],
                            }),
                            title: intl('lab'),
                        }}
                        name={appNavigationNames.profiles.labHistory}
                        component={screens.LabHistoryScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                            }),
                            title: intl('profile'),
                        }}
                        name={appNavigationNames.profiles.clientProfile}
                        component={screens.ClientProfileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profileNoChatButton,
                            }),
                            title: intl('editClientProfileGroups'),
                        }}
                        name={appNavigationNames.profiles.editClientProfileGroups}
                        component={screens.EditClientProfileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['client-records', 'client-record'],
                            }),
                            title: intl('clientRecord'),
                            contentStyle: { backgroundColor: colors.Background },
                        }}
                        name={appNavigationNames.profiles.clientRecord}
                        component={screens.ClientRecordScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('addClientRecord'),
                        }}
                        name={appNavigationNames.profiles.addClientRecord}
                        component={screens.AddClientRecordScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('addClientInvite'),
                        }}
                        name={appNavigationNames.profiles.addClientInvitation}
                        component={screens.AddClientInvitationScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('bulkClientInvite'),
                        }}
                        name={appNavigationNames.profiles.bulkInviteClients}
                        component={screens.BulkInviteClientsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('editClientRecord'),
                        }}
                        name={appNavigationNames.profiles.editClientRecord}
                        component={screens.EditClientRecordScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('addClientRecordInvite'),
                        }}
                        name={appNavigationNames.profiles.addClientRecordInvitation}
                        component={screens.AddClientRecordInvitationScreen}
                        initialParams={capturedUrlUTMParameters}
                    />

                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: [
                                    'client-records',
                                    'client-record',
                                    'client-record-notes',
                                    'add-client-record-note',
                                ],
                            }),
                            title: intl('addClientRecordNoteScreen'),
                        }}
                        name={appNavigationNames.profiles.addClientRecordNote}
                        component={screens.AddClientRecordNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: [
                                    'client-records',
                                    'client-record',
                                    'client-record-notes',
                                    'client-record-note',
                                ],
                            }),
                            title: intl('clientRecordNoteDetailsScreen'),
                        }}
                        name={appNavigationNames.profiles.clientRecordNote}
                        component={screens.ClientRecordNoteDetailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: [
                                    'client-records',
                                    'client-record',
                                    'client-record-notes',
                                    'client-record-note',
                                    'edit-client-record-note',
                                ],
                            }),
                            title: intl('editClientRecordNoteScreen'),
                        }}
                        name={appNavigationNames.profiles.editClientRecordNote}
                        component={screens.EditClientRecordNoteScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('copyOrgNotesToClients'),
                        }}
                        name={appNavigationNames.profiles.copyOrgNoteToClients}
                        component={screens.CopyOrgNotesToClientsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('copyOrgCareTeamMemberToClients'),
                        }}
                        name={appNavigationNames.profiles.copyOrgCareTeamMemberToClients}
                        component={screens.CopyOrgCareTeamMemberToClientsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('sendClientsMessage'),
                        }}
                        name={appNavigationNames.profiles.sendClientsMessage}
                        component={screens.SendClientsMessageScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('messagePreferences'),
                        }}
                        name={appNavigationNames.profiles.messagePreferences}
                        component={screens.MessagePreferencesScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('clientsActivity'),
                        }}
                        name={appNavigationNames.profiles.clientsActivity}
                        component={screens.ClientsActivityScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('orgInviteAcceptSuccess'),
                        }}
                        name={appNavigationNames.profiles.orgInviteAcceptSuccess}
                        component={screens.OrgInviteAcceptSuccessScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('activityDetails'),
                        }}
                        name={appNavigationNames.profiles.clientActivityDetails}
                        component={screens.ClientActivityDetailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile'],
                            }),
                            title: intl('profileActivity'),
                        }}
                        name={appNavigationNames.profiles.profileActivity}
                        component={screens.ProfileActivityScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'profile-activity', 'audit-log'],
                            }),
                            title: intl('profileActivityDetails'),
                        }}
                        name={appNavigationNames.profiles.profileActivityDetails}
                        component={screens.ProfileActivityDetailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'profile-activity', 'message-profile'],
                            }),
                            title: intl('messageProfile'),
                        }}
                        name={appNavigationNames.profiles.messageProfile}
                        component={screens.MessageProfileScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'client-record', 'client-activity-history'],
                            }),
                            title: intl('clientActivityHistory'),
                        }}
                        name={appNavigationNames.profiles.clientActivityHistory}
                        component={screens.ClientActivityHistoryScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                            }),
                            title: intl('copyVisitToClients'),
                        }}
                        name={appNavigationNames.profiles.copyVisitToClients}
                        component={screens.CopyVisitToClientsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile', 'access'],
                            }),
                            title: intl('access'),
                        }}
                        name={appNavigationNames.profiles.access}
                        component={screens.AccessScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile', 'messages'],
                            }),
                            title: intl('messages'),
                        }}
                        name={appNavigationNames.profiles.messages}
                        component={screens.MessagesScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile', 'connections'],
                            }),
                            title: intl('connect'),
                        }}
                        name={appNavigationNames.profiles.connect}
                        component={screens.ConnectScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.profile,
                                breadCrumbs: ['profiles-root', 'profile', 'static-profile-notes'],
                            }),
                            title: intl('profileNotes'),
                        }}
                        name={appNavigationNames.profiles.profileNotes}
                        component={screens.ProfileNotesScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'bulk-add-client-records'],
                            }),
                            title: intl('bulkAddClientRecords'),
                        }}
                        name={appNavigationNames.profiles.bulkAddClientRecords}
                        initialParams={capturedUrlUTMParameters}
                        component={screens.BulkAddClientRecordsScreen}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile', 'add-suggested-folders'],
                            }),
                            title: intl('addSuggestedFolders'),
                        }}
                        name={appNavigationNames.profiles.addSuggestedProfileFolders}
                        component={screens.AddProfileSuggestedFoldersScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile', 'edit-client-invitation'],
                            }),
                            title: intl('editClientInvitation'),
                        }}
                        name={appNavigationNames.profiles.editClientInvitation}
                        component={screens.EditClientInvitationScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'bulk-add-client-profiles-groups'],
                            }),
                            title: intl('bulkAddClientProfilesGroups'),
                        }}
                        name={appNavigationNames.profiles.bulkAddClientProfilesGroups}
                        component={screens.BulkAddClientRecordsProfilesScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['client-records', 'client-record'],
                            }),
                            title: intl('editClientBillingInformation'),
                            contentStyle: { backgroundColor: colors.Background },
                        }}
                        name={appNavigationNames.profiles.editClientBillingInfo}
                        component={screens.EditClientBillingInfoScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['client-records', 'client-record'],
                            }),
                            title: intl('clientInvoiceItems'),
                            contentStyle: { backgroundColor: colors.Background },
                        }}
                        name={appNavigationNames.profiles.clientInvoiceItems}
                        component={screens.ClientInvoiceItemsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                    <Stack.Screen
                        options={{
                            header: getHeader({
                                mobile: mobileHeaderConfigs.standard,
                                breadCrumbs: ['profiles-root', 'profile', 'visit-invite-emails'],
                            }),
                            title: intl('subscribeToVisitInvites'),
                            contentStyle: { backgroundColor: colors.Background },
                        }}
                        name={appNavigationNames.profiles.visitInviteEmails}
                        component={screens.VisitInviteEmailsScreen}
                        initialParams={capturedUrlUTMParameters}
                    />
                </Stack.Navigator>
            </ProfileSideTabNavigation>
        </MaybeDrawerLayout>
    );
};

export default withActiveTab(ProfilesNavigation, 'clients-or-profiles');
